import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, Paperclip, X, FileIcon, Smile, Phone, Video, MoreVertical, ArrowLeft, Trash2, MessageCircle, Check, CheckCheck, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import Navigation from "@/components/Navigation";
import { useToast } from "@/hooks/use-toast";
import { notifyNewMessage } from "@/lib/notifications";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { useVideoCallContext } from "@/components/VideoCallProvider";

interface Message {
  id: string;
  content: string;
  sender_id: string;
  created_at: string;
  read_at?: string | null;
  attachment_url?: string;
  attachment_type?: string;
}

interface Conversation {
  id: string;
  user1_id: string;
  user2_id: string;
  unreadCount?: number;
  lastMessage?: {
    content: string;
    sender_id: string;
    created_at: string;
  };
  profiles?: {
    id: string;
    name: string;
    avatar_url: string;
    is_available?: boolean;
  };
}

const Chat = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { startCall, isLoading: isCallLoading } = useVideoCallContext();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [typingInConversations, setTypingInConversations] = useState<Record<string, boolean>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleStartVideoCall = async () => {
    if (!selectedConv) return;
    const receiverId = selectedConv.user1_id === user?.id ? selectedConv.user2_id : selectedConv.user1_id;
    await startCall(receiverId);
  };

  useEffect(() => {
    if (!user) return;

    const fetchConversations = async () => {
      const { data: convData, error } = await supabase
        .from("conversations")
        .select("*")
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
        .order("updated_at", { ascending: false });

      if (error) {
        console.error("Error fetching conversations:", error);
        return;
      }

      if (!convData) return;

      // Deduplicate conversations - keep only the first one for each user pair
      const seenUserPairs = new Set<string>();
      const uniqueConversations = convData.filter((conv) => {
        const userPair = [conv.user1_id, conv.user2_id].sort().join('-');
        if (seenUserPairs.has(userPair)) {
          return false;
        }
        seenUserPairs.add(userPair);
        return true;
      });

      const conversationsWithProfiles = await Promise.all(
        uniqueConversations.map(async (conv) => {
          const otherUserId = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
          
          // Fetch profile
          const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("id, name, avatar_url, is_available")
            .eq("id", otherUserId)
            .single();

          if (profileError) {
            console.error("Error fetching profile:", profileError);
          }

          // Fetch unread count for this conversation
          const { count: unreadCount } = await supabase
            .from("messages")
            .select("*", { count: "exact", head: true })
            .eq("conversation_id", conv.id)
            .neq("sender_id", user.id)
            .is("read_at", null);

          // Fetch last message for this conversation
          const { data: lastMessageData } = await supabase
            .from("messages")
            .select("content, sender_id, created_at")
            .eq("conversation_id", conv.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .single();

          return {
            ...conv,
            unreadCount: unreadCount || 0,
            lastMessage: lastMessageData || undefined,
            profiles: profile || { id: otherUserId, name: "User", avatar_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${otherUserId}`, is_available: false },
          };
        })
      );

      setConversations(conversationsWithProfiles);

      // Check if userId is in URL params and auto-select/create conversation
      const userId = searchParams.get('userId');
      if (userId) {
        // Check for existing conversation between current user and target user
        const existingConv = conversationsWithProfiles.find(
          conv => 
            (conv.user1_id === userId && conv.user2_id === user.id) ||
            (conv.user2_id === userId && conv.user1_id === user.id)
        );

        if (existingConv) {
          setSelectedConversation(existingConv.id);
        } else {
          // Create new conversation
          const { data: newConv, error: convError } = await supabase
            .from('conversations')
            .insert({
              user1_id: user.id,
              user2_id: userId,
            })
            .select()
            .single();

          if (!convError && newConv) {
            setSelectedConversation(newConv.id);
            // Refresh conversations
            fetchConversations();
          }
        }
      }
    };

    fetchConversations();
  }, [user, searchParams]);

  // Subscribe to typing indicators for all conversations
  useEffect(() => {
    if (!user || conversations.length === 0) return;

    const channels = conversations.map(conv => {
      const channel = supabase.channel(`typing-list:${conv.id}`);
      
      channel
        .on('presence', { event: 'sync' }, () => {
          const state = channel.presenceState();
          const someoneTyping = Object.values(state).flat().some(
            (presence: any) => presence.user_id !== user.id && presence.typing
          );
          setTypingInConversations(prev => ({ ...prev, [conv.id]: someoneTyping }));
        })
        .subscribe();
      
      return channel;
    });

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [user, conversations.length]);

  useEffect(() => {
    if (!selectedConversation) return;

    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", selectedConversation)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching messages:", error);
        return;
      }

      setMessages(data || []);
      
      // Mark unread messages as read
      if (user && data && data.length > 0) {
        const unreadMessageIds = data
          .filter(msg => msg.sender_id !== user.id && !msg.read_at)
          .map(msg => msg.id);
        
        if (unreadMessageIds.length > 0) {
          await supabase
            .from("messages")
            .update({ read_at: new Date().toISOString() })
            .in("id", unreadMessageIds);
        }
      }
      
      // Scroll to bottom after messages load
      setTimeout(() => {
        if (scrollAreaRef.current) {
          const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
          if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }
        }
      }, 100);
    };

    fetchMessages();

    const channel = supabase
      .channel(`messages:${selectedConversation}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${selectedConversation}`,
        },
        (payload) => {
          const newMsg = payload.new as Message;
          setMessages((current) => {
            // Prevent duplicates
            if (current.some(msg => msg.id === newMsg.id)) {
              return current;
            }
            return [...current, newMsg];
          });
          
          // Scroll to bottom on new message
          setTimeout(() => {
            if (scrollAreaRef.current) {
              const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
              if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
              }
            }
          }, 100);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConversation]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10MB",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadFile = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `${user?.id}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('profile-images')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('profile-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  // Realtime typing indicator using presence
  useEffect(() => {
    if (!selectedConversation || !user) return;

    const channel = supabase.channel(`typing-list:${selectedConversation}`);
    
    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const otherUsersTyping = Object.values(state).flat().some(
          (presence: any) => presence.user_id !== user.id && presence.typing
        );
        setOtherUserTyping(otherUsersTyping);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConversation, user]);

  const handleTyping = async (text: string) => {
    setNewMessage(text);
    
    if (!selectedConversation || !user) return;
    
    const channel = supabase.channel(`typing-list:${selectedConversation}`);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Track typing status
    if (text.length > 0) {
      await channel.track({ user_id: user.id, typing: true });
    }
    
    // Set timeout to clear typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(async () => {
      await channel.track({ user_id: user.id, typing: false });
    }, 2000);
  };

  const deleteConversation = async (conversationId: string) => {
    try {
      // Delete all messages in the conversation first
      await supabase
        .from('messages')
        .delete()
        .eq('conversation_id', conversationId);

      // Delete the conversation
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);

      if (error) throw error;

      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (selectedConversation === conversationId) {
        setSelectedConversation(null);
      }
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
      
      toast({
        title: "Conversation deleted",
        description: "The conversation has been permanently deleted.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete conversation",
        variant: "destructive",
      });
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation || !user) return;
    setUploadingFile(true);

    try {
      let attachmentUrl = null;
      let attachmentType = null;

      if (selectedFile) {
        attachmentUrl = await uploadFile(selectedFile);
        attachmentType = selectedFile.type;
      }

      const { error } = await supabase.from("messages").insert({
        conversation_id: selectedConversation,
        sender_id: user.id,
        content: newMessage.trim() || "(Attachment)",
        attachment_url: attachmentUrl,
        attachment_type: attachmentType,
      });

      if (error) throw error;

      // Send push notification to the other user
      const conv = conversations.find(c => c.id === selectedConversation);
      if (conv) {
        const receiverId = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
        const { data: senderProfile } = await supabase.from('profiles').select('name').eq('id', user.id).single();
        notifyNewMessage(receiverId, senderProfile?.name || 'Someone');
      }

      setNewMessage("");
      setSelectedFile(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const selectedConv = conversations.find(c => c.id === selectedConversation);

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <div className="flex h-[calc(100vh-4rem)] md:h-screen">
        {/* Sidebar - Conversation List */}
        <div className={`${selectedConversation ? 'hidden md:flex' : 'flex'} md:w-80 w-full flex-col border-r border-border`}>
          <div className="gradient-primary text-white p-6 pb-8 rounded-b-3xl shadow-strong">
            <div className="flex items-center gap-3 mb-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20 -ml-2"
                onClick={() => window.history.back()}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <MessageCircle className="h-6 w-6" />
                Messages
              </h1>
            </div>
            <p className="text-white/90 text-sm ml-9">Connect with your skill partners</p>
          </div>
          
          <ScrollArea className="flex-1">
            {conversations.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-muted-foreground">No conversations yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {conversations.map((conv) => {
                  const isSelected = conv.id === selectedConversation;
                  const isAvailable = conv.profiles?.is_available;
                  const unreadCount = conv.unreadCount || 0;
                  const isTyping = typingInConversations[conv.id] || false;
                  return (
                    <div
                      key={conv.id}
                      className={`p-4 cursor-pointer transition-all hover:bg-accent/50 border-l-4 group ${
                        isSelected ? 'bg-accent/30 border-primary' : 'border-transparent'
                      }`}
                      onClick={() => {
                        setSelectedConversation(conv.id);
                        // Clear unread count locally when selecting conversation
                        setConversations(prev => prev.map(c => 
                          c.id === conv.id ? { ...c, unreadCount: 0 } : c
                        ));
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Avatar className="h-12 w-12 ring-2 ring-background">
                            <AvatarImage src={conv.profiles?.avatar_url} />
                            <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white">
                              {conv.profiles?.name?.charAt(0) || "?"}
                            </AvatarFallback>
                          </Avatar>
                          {isAvailable && (
                            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-background" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold truncate">{conv.profiles?.name}</p>
                            {conv.lastMessage && (
                              <span className="text-[10px] text-muted-foreground ml-2 shrink-0">
                                {new Date(conv.lastMessage.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                          <p className={`text-xs truncate ${unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {isTyping ? (
                              <span className="text-primary italic flex items-center gap-1">
                                <span className="flex gap-0.5">
                                  <span className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                  <span className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                  <span className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </span>
                                typing...
                              </span>
                            ) : conv.lastMessage ? (
                              <>
                                {conv.lastMessage.sender_id === user?.id && <span className="text-muted-foreground">You: </span>}
                                {conv.lastMessage.content}
                              </>
                            ) : (
                              isAvailable ? (
                                <span className="flex items-center gap-1">
                                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                  Active now
                                </span>
                              ) : 'Start a conversation'
                            )}
                          </p>
                        </div>
                        {unreadCount > 0 && (
                          <Badge variant="default" className="bg-primary text-primary-foreground h-5 min-w-5 px-1.5 text-xs font-bold rounded-full">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConversationToDelete(conv.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Main Chat Area */}
        <div className={`${selectedConversation ? 'flex' : 'hidden md:flex'} flex-1 flex-col`}>
          {selectedConversation && selectedConv ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-card to-secondary/20">
                <div className="flex items-center gap-3 flex-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="relative">
                    <Avatar className="h-10 w-10 ring-2 ring-primary/20">
                      <AvatarImage src={selectedConv.profiles?.avatar_url} />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white">
                        {selectedConv.profiles?.name?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-background" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold">{selectedConv.profiles?.name}</h2>
                      <div className="flex items-center gap-1">
                      {otherUserTyping ? (
                        <div className="flex items-center gap-1 text-xs text-primary">
                          <div className="flex gap-0.5">
                            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                          <span className="ml-1">typing...</span>
                        </div>
                      ) : (
                        <p className="text-xs text-green-500 flex items-center gap-1">
                          <span className="w-2 h-2 bg-green-500 rounded-full" />
                          Active now
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-9 w-9"
                    onClick={handleStartVideoCall}
                    disabled={isCallLoading}
                    title="Start Video Call"
                  >
                    {isCallLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Video className="h-4 w-4" />
                    )}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-9 w-9">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        className="text-destructive focus:text-destructive"
                        onClick={() => {
                          setConversationToDelete(selectedConversation);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Conversation
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Messages Area */}
              <ScrollArea ref={scrollAreaRef} className="flex-1 p-4 bg-gradient-to-b from-background to-secondary/10">
                <div className="space-y-4">
                  {messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4">
                        <Send className="h-10 w-10 text-primary" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2">Start the conversation!</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Say hi and break the ice 👋
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center max-w-md">
                        <Badge 
                          variant="outline" 
                          className="cursor-pointer hover:bg-primary hover:text-white transition-colors"
                          onClick={() => setNewMessage("Hey! Ready to learn?")}
                        >
                          👋 Hey! Ready to learn?
                        </Badge>
                        <Badge 
                          variant="outline" 
                          className="cursor-pointer hover:bg-primary hover:text-white transition-colors"
                          onClick={() => setNewMessage("What skill are you most excited about?")}
                        >
                          🎯 What skill are you most excited about?
                        </Badge>
                        <Badge 
                          variant="outline" 
                          className="cursor-pointer hover:bg-primary hover:text-white transition-colors"
                          onClick={() => setNewMessage("When's a good time to connect?")}
                        >
                          ⏰ When's a good time to connect?
                        </Badge>
                      </div>
                    </div>
                  )}
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.sender_id === user?.id
                          ? "justify-end"
                          : "justify-start"
                      }`}
                    >
                      <div
                        className={`inline-block p-3 rounded-2xl max-w-[70%] shadow-sm transition-all hover:shadow-md ${
                          message.sender_id === user?.id
                            ? "bg-gradient-to-br from-primary to-primary/80 text-white rounded-br-sm"
                            : "bg-card border border-border rounded-bl-sm"
                        }`}
                      >
                        <p className="break-words">{message.content}</p>
                        {message.attachment_url && (
                          <div className="mt-2">
                            {message.attachment_type?.startsWith('image/') ? (
                              <img 
                                src={message.attachment_url} 
                                alt="Attachment" 
                                className="max-w-full rounded-lg cursor-pointer hover:opacity-80"
                                onClick={() => window.open(message.attachment_url, '_blank')}
                              />
                            ) : (
                              <a 
                                href={message.attachment_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm underline"
                              >
                                <FileIcon className="h-4 w-4" />
                                View attachment
                              </a>
                            )}
                          </div>
                        )}
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <span className="text-xs opacity-70">
                            {new Date(message.created_at).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </span>
                          {message.sender_id === user?.id && (
                            message.read_at ? (
                              <CheckCheck className="h-3.5 w-3.5 text-blue-400" />
                            ) : (
                              <Check className="h-3.5 w-3.5 opacity-70" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Message Input Area */}
              <div className="p-4 border-t border-border bg-card">
                <div className="space-y-2">
                  {selectedFile && (
                    <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-xl border border-border">
                      <FileIcon className="h-4 w-4 text-primary" />
                      <span className="text-sm flex-1 truncate">{selectedFile.name}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => setSelectedFile(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2 items-end">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileSelect}
                      accept="image/*,.pdf,.doc,.docx"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      className="shrink-0 h-10 w-10"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="shrink-0 h-10 w-10"
                          disabled={uploadingFile}
                        >
                          <Smile className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-2" side="top" align="start">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {['😀', '😂', '😍', '🥰', '😎', '🤔', '😢', '😡', '👍', '👎', '❤️', '🔥', '🎉', '👏', '🙏', '💪'].map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => setNewMessage(prev => prev + emoji)}
                              className="text-2xl hover:scale-110 transition-transform p-1 hover:bg-secondary rounded"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Input
                      value={newMessage}
                      onChange={(e) => handleTyping(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 rounded-xl"
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                      disabled={uploadingFile}
                    />
                    <Button 
                      onClick={sendMessage} 
                      size="icon" 
                      className="shrink-0 h-10 w-10 bg-gradient-to-r from-primary to-accent hover:opacity-90"
                      disabled={uploadingFile || (!newMessage.trim() && !selectedFile)}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-background to-secondary/20">
              <div className="text-center max-w-md">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                  <Send className="h-12 w-12 text-primary" />
                </div>
                <h2 className="text-2xl font-semibold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Your Messages
                </h2>
                <p className="text-muted-foreground mb-6">
                  Select a conversation to start chatting and connecting with your skill partners
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Badge variant="secondary">💬 Real-time messaging</Badge>
                  <Badge variant="secondary">📎 Share files</Badge>
                  <Badge variant="secondary">🎯 Connect & learn</Badge>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Delete Conversation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this conversation and all messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConversationToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => conversationToDelete && deleteConversation(conversationToDelete)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <div className="md:hidden">
        <Navigation />
      </div>
    </div>
  );
};

export default Chat;
